import { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, AlertCircle, Calendar, Download, RefreshCw, X, CheckCircle } from 'lucide-react';
import api from '../../api/apiClient';

interface LedgerSummary {
  expectedRent: number;
  receivedRent: number;
  arrears: number;
  netIncome: number;
  upcomingPayouts: any[];
}

export default function OwnerRentLedger() {
  const [summary, setSummary] = useState<LedgerSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'payments' | 'arrears' | 'payouts'>('overview');
  const [reconciling, setReconciling] = useState(false);
  const [modal, setModal] = useState<{ show: boolean; success: boolean; message: string }>({ show: false, success: true, message: '' });

  useEffect(() => {
    loadOverview();
  }, []);

  const loadOverview = async () => {
    try {
      const response = await api.get<LedgerSummary>('/owner/ledger/overview');
      setSummary(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const response = await api.post<{ reconciledCount: number; reconciledAmount: number }>(
        '/owner/ledger/reconcile',
        {}
      );
      setModal({ show: true, success: true, message: `Reconciled ${response.reconciledCount} transaction${response.reconciledCount !== 1 ? 's' : ''} totalling $${response.reconciledAmount.toFixed(2)}` });
      loadOverview();
    } catch (err: any) {
      setModal({ show: true, success: false, message: 'Reconciliation failed: ' + err.message });
    } finally {
      setReconciling(false);
    }
  };

  const downloadYearEndStatement = async () => {
    const year = new Date().getFullYear();
    try {
      const response = await api.get<any>(`/owner/ledger/year-end-statement?year=${year}`);
      // Convert to CSV
      const data = Array.isArray(response) ? response : (response.entries || response.payments || [response]);
      const headers = data.length > 0 ? Object.keys(data[0]).join(',') : 'date,description,amount';
      const rows = data.map((row: any) =>
        Object.values(row).map((v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')
      );
      const csv = [headers, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rent-statement-${year}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setModal({ show: true, success: false, message: 'Failed to generate statement: ' + err.message });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F8F8' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#0C4432' }}></div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F8F8' }}>
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#DF6C4F' }} />
          <p className="text-xl font-semibold" style={{ color: '#222' }}>{error || 'Failed to load ledger'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#F8F8F8' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <DollarSign className="w-8 h-8" style={{ color: '#0C4432' }} />
            <div>
              <h1 className="text-3xl font-bold" style={{ color: '#0C4432' }}>Rent Ledger</h1>
              <p style={{ color: '#666' }}>Complete financial overview and reconciliation</p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleReconcile}
              disabled={reconciling}
              className="flex items-center gap-2 px-4 py-2.5 rounded-10 text-[13px] font-semibold text-white bg-forest-900 hover:bg-forest-800 transition-colors shadow-sm disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${reconciling ? 'animate-spin' : ''}`} />
              {reconciling ? 'Reconciling...' : 'Reconcile'}
            </button>
            <button
              onClick={downloadYearEndStatement}
              className="flex items-center gap-2 px-4 py-2.5 rounded-10 text-[13px] font-semibold text-white bg-forest-900 hover:bg-forest-800 transition-colors shadow-sm"
            >
              <Download className="w-4 h-4" />
              Year-End Statement
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-6 h-6" style={{ color: '#0C4432' }} />
              <span className="text-xs font-medium text-gray-500">Expected</span>
            </div>
            <p className="text-2xl font-bold" style={{ color: '#0C4432' }}>
              ${summary.expectedRent.toFixed(2)}
            </p>
            <p className="text-sm mt-1" style={{ color: '#666' }}>Expected Rent</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-6 h-6" style={{ color: '#49c5b6' }} />
              <span className="text-xs font-medium text-gray-500">Received</span>
            </div>
            <p className="text-2xl font-bold" style={{ color: '#49c5b6' }}>
              ${summary.receivedRent.toFixed(2)}
            </p>
            <p className="text-sm mt-1" style={{ color: '#666' }}>Incoming Payments</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <AlertCircle className="w-6 h-6" style={{ color: '#DF6C4F' }} />
              <span className="text-xs font-medium text-gray-500">Overdue</span>
            </div>
            <p className="text-2xl font-bold" style={{ color: '#DF6C4F' }}>
              ${summary.arrears.toFixed(2)}
            </p>
            <p className="text-sm mt-1" style={{ color: '#666' }}>Arrears</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-6 h-6" style={{ color: '#0C4432' }} />
              <span className="text-xs font-medium text-gray-500">Net</span>
            </div>
            <p className="text-2xl font-bold" style={{ color: '#0C4432' }}>
              ${summary.netIncome.toFixed(2)}
            </p>
            <p className="text-sm mt-1" style={{ color: '#666' }}>Net Income</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b">
            <div className="flex">
              {(['overview', 'payments', 'arrears', 'payouts'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 font-medium capitalize ${activeTab === tab ? 'border-b-2' : ''}`}
                  style={{
                    color: activeTab === tab ? '#0C4432' : '#666',
                    borderColor: activeTab === tab ? '#0C4432' : 'transparent'
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3" style={{ color: '#0C4432' }}>
                    Collection Rate
                  </h3>
                  <div className="relative pt-1">
                    <div className="flex mb-2 items-center justify-between">
                      <div>
                        <span className="text-xs font-semibold inline-block" style={{ color: '#0C4432' }}>
                          {summary.expectedRent > 0
                            ? ((summary.receivedRent / summary.expectedRent) * 100).toFixed(1)
                            : 0}%
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-semibold inline-block" style={{ color: '#666' }}>
                          ${summary.receivedRent.toFixed(2)} / ${summary.expectedRent.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="overflow-hidden h-2 text-xs flex rounded" style={{ backgroundColor: '#E5E7EB' }}>
                      <div
                        style={{
                          width: `${summary.expectedRent > 0 ? (summary.receivedRent / summary.expectedRent) * 100 : 0}%`,
                          backgroundColor: '#49c5b6'
                        }}
                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3" style={{ color: '#0C4432' }}>
                    Upcoming Payouts
                  </h3>
                  {summary.upcomingPayouts.length === 0 ? (
                    <p style={{ color: '#666' }}>No upcoming payouts scheduled</p>
                  ) : (
                    <div className="space-y-2">
                      {summary.upcomingPayouts.map((payout: any, idx: number) => (
                        <div key={idx} className="flex items-center justify-between p-3 rounded-md" style={{ backgroundColor: '#F8F8F8' }}>
                          <div className="flex items-center gap-3">
                            <Calendar className="w-5 h-5" style={{ color: '#666' }} />
                            <div>
                              <p className="font-medium" style={{ color: '#222' }}>
                                ${payout.amount.toFixed(2)}
                              </p>
                              <p className="text-sm" style={{ color: '#666' }}>
                                {payout.payoutDate ? new Date(payout.payoutDate).toLocaleDateString() : 'TBD'}
                              </p>
                            </div>
                          </div>
                          <span
                            className="px-3 py-1 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: payout.isReconciled ? '#E8F5F3' : '#FEF3C7',
                              color: payout.isReconciled ? '#0C4432' : '#F59E0B'
                            }}
                          >
                            {payout.isReconciled ? 'Reconciled' : 'Pending'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'payments' && (
              <div>
                <h3 className="text-lg font-semibold mb-3" style={{ color: '#0C4432' }}>
                  Incoming Payments
                </h3>
                <p style={{ color: '#666' }}>
                  Total received: ${summary.receivedRent.toFixed(2)}
                </p>
                <p className="text-sm mt-1" style={{ color: '#999' }}>
                  Payment details and history available in detailed view
                </p>
              </div>
            )}

            {activeTab === 'arrears' && (
              <div>
                <h3 className="text-lg font-semibold mb-3" style={{ color: '#0C4432' }}>
                  Arrears & Overdue
                </h3>
                {summary.arrears > 0 ? (
                  <div className="p-4 rounded-md" style={{ backgroundColor: '#FEE' }}>
                    <p className="font-semibold" style={{ color: '#DF6C4F' }}>
                      Total Arrears: ${summary.arrears.toFixed(2)}
                    </p>
                    <p className="text-sm mt-1" style={{ color: '#666' }}>
                      Action required: Follow up with tenants or initiate collection procedures
                    </p>
                  </div>
                ) : (
                  <p style={{ color: '#49c5b6' }}>✓ No arrears - all payments up to date</p>
                )}
              </div>
            )}

            {activeTab === 'payouts' && (
              <div>
                <h3 className="text-lg font-semibold mb-3" style={{ color: '#0C4432' }}>
                  Payout Schedule
                </h3>
                {summary.upcomingPayouts.length === 0 ? (
                  <p style={{ color: '#666' }}>No payouts scheduled</p>
                ) : (
                  <div className="space-y-2">
                    {summary.upcomingPayouts.map((payout: any, idx: number) => (
                      <div key={idx} className="flex items-center justify-between p-4 border rounded-md">
                        <div>
                          <p className="font-semibold" style={{ color: '#0C4432' }}>
                            ${payout.amount.toFixed(2)}
                          </p>
                          <p className="text-sm" style={{ color: '#666' }}>
                            Due: {payout.payoutDate ? new Date(payout.payoutDate).toLocaleDateString() : 'TBD'}
                          </p>
                        </div>
                        <span
                          className="px-3 py-1 rounded-full text-sm font-medium"
                          style={{
                            backgroundColor: payout.isReconciled ? '#E8F5F3' : '#F0F4F8',
                            color: payout.isReconciled ? '#0C4432' : '#666'
                          }}
                        >
                          {payout.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-3" style={{ color: '#0C4432' }}>
            Auto-Reconciliation
          </h3>
          <p style={{ color: '#666' }}>
            The system automatically matches incoming payments with scheduled payouts to maintain accurate financial records.
            Click "Reconcile" to process pending transactions and update the ledger.
          </p>
          <div className="mt-4 p-3 rounded-md" style={{ backgroundColor: '#E8F5F3' }}>
            <p className="text-sm" style={{ color: '#0C4432' }}>
              <strong>Tip:</strong> Run reconciliation weekly to ensure accurate financial reporting and tax compliance.
            </p>
          </div>
        </div>
      </div>

      {/* Reconcile result modal */}
      {modal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-12 shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-start gap-3">
              {modal.success
                ? <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                : <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />}
              <div className="flex-1">
                <h3 className="font-semibold text-[15px]" style={{ color: '#0C4432' }}>
                  {modal.success ? 'Reconciliation Complete' : 'Reconciliation Failed'}
                </h3>
                <p className="text-[13px] text-gray-600 mt-1">{modal.message}</p>
              </div>
              <button onClick={() => setModal(m => ({ ...m, show: false }))} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setModal(m => ({ ...m, show: false }))}
                className="px-4 py-2 rounded-10 text-[13px] font-semibold text-white bg-forest-900 hover:bg-forest-800 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

